import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

// Iter181 / Iter186 — Public viewer: unauthenticated big-screen board.
// Prefers a WebSocket push (1s server tick) with automatic fallback to
// HTTP polling every 3s if the WS handshake fails. Timers tick locally
// between messages for a smooth countdown.
// Iter246 — Auto-scroll loop for wall displays. When there are more
// courts than fit on screen, the viewer slow-scrolls through the whole
// board, pauses briefly at the bottom, snaps back to the top, pauses
// again, then repeats. Speed is set per-festival by the admin in Live
// Ops (config.viewer_scroll_speed: off / slow / medium / fast). WS
// updates continue to land during the scroll without breaking it —
// scroll position and payload state live in independent effects.
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const WS_BACKEND = BACKEND ? BACKEND.replace(/^http/, "ws") : "";

// Pixels-per-second for the auto-scroll animation. Tuned for glance-
// across-the-room readability on a wall-mounted TV; a whole 8-court
// board loops in ~20-30s on "medium". Bumped in iter251 from the
// earlier 12/30/70 which was too subtle to notice on a 65-inch+ TV.
const SCROLL_SPEED_PX_S = { slow: 25, medium: 55, fast: 110 };

const STATE_STYLES = {
  live: "bg-[#FF3B30] text-white",
  finished: "bg-[#FFB020] text-black animate-pulse",
  idle: "bg-[#141414] text-[#A3A3A3]",
  waiting: "bg-[#0F2A4B] text-[#66C2FF]",
  ready: "bg-[#0F4B0F] text-[#CCFF00]",
  paused: "bg-[#141414] text-[#525252]",
  // Legacy aliases pre-Iter185.
  timeout: "bg-[#FFB020] text-black animate-pulse",
  assigned: "bg-[#0F2A4B] text-[#66C2FF]",
  pre_start: "bg-[#0F4B0F] text-[#CCFF00]",
};

const BAND_INK = {
  green: "text-[#4CAF50]",
  blue: "text-[#2196F3]",
  red: "text-[#F44336]",
  spare: "text-[#FFB020]",
};

const fmt = (s) => {
  if (s == null) return "--:--";
  const total = Math.max(0, Math.floor(s));
  const m = Math.floor(total / 60).toString().padStart(2, "0");
  const sec = (total % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};

function PlayerLine({ p, ratingMode, scale = 1 }) {
  if (!p) return null;
  const name = typeof p === "string" ? p : p.name;
  const showRating = typeof p === "object" && ratingMode === "dupr" && p.rating != null;
  const showBand = typeof p === "object" && ratingMode === "band" && p.band;
  return (
    <div className="flex items-center justify-center gap-2 min-w-0">
      <span
        className="font-semibold leading-tight truncate min-w-0"
        style={{ fontSize: `${1.4 * scale}rem`, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}
        title={name}
        data-testid="viewer-player-name"
      >{name}</span>
      {showRating && <span className="font-mono text-[#525252] shrink-0" style={{ fontSize: `${0.75 * scale}rem` }}>({p.rating})</span>}
      {showBand && <span className={`uppercase tracking-widest shrink-0 ${BAND_INK[p.band] || "text-[#525252]"}`} style={{ fontSize: `${0.7 * scale}rem` }}>●</span>}
    </div>
  );
}

export default function FestivalPublicViewer() {
  const { id } = useParams();
  // Iter368 · Fix 2 — External public viewer is a big-screen wall
  // display; it must always render in dark mode regardless of the
  // organiser's preferred theme on their personal device. The
  // ThemeProvider's `applyToDom` special-cases this route so no
  // child-level DOM mutation is required here — an authenticated
  // organiser on a light-mode device opening /festival/:id/viewer
  // still sees a dark board. If a future refactor moves the route
  // matcher out of ThemeProvider, the child effect below can be
  // restored as a belt-and-braces safety net.
  // Iter313 — Dual-zone sponsor rotation (header + footer). Each
  // zone owns its own interval + fade state inside `SponsorZone`
  // so the header and footer cycle independently.
  // Iter294/295/297 — Courts-per-row control (1-6, no AUTO). Sync
  // rules:
  //   · If the local viewer has previously clicked one of the 1-6
  //     buttons, `rl_viewer_cpr_user_${id}` = "1" and their value in
  //     `rl_viewer_cpr_${id}` wins forever (never overridden by OPS).
  //   · Otherwise the value tracks `data.config.viewer_columns` set
  //     from OPS `SCREEN ROWS` (default 4 when unset).
  const [courtsPerRow, setCourtsPerRow] = useState(() => {
    try {
      const userFlag = window.localStorage.getItem(`rl_viewer_cpr_user_${id}`);
      if (userFlag === "1") {
        const n = Number(window.localStorage.getItem(`rl_viewer_cpr_${id}`));
        if (n >= 1 && n <= 6) return n;
      }
    } catch {}
    return 4;
  });
  const setCpr = (n) => {
    const clamped = Math.max(1, Math.min(6, Number(n) || 4));
    setCourtsPerRow(clamped);
    try {
      window.localStorage.setItem(`rl_viewer_cpr_user_${id}`, "1");
      window.localStorage.setItem(`rl_viewer_cpr_${id}`, String(clamped));
    } catch {}
  };
  // Font-scale multiplier — baseline at 4 cols.
  const scale = { 1: 1.55, 2: 1.3, 3: 1.12, 4: 1.0, 5: 0.82, 6: 0.68 }[courtsPerRow] || 1;
  const [data, setData] = useState(null);
  const [muted, setMuted] = useState(false);
  const [live, setLive] = useState(false); // WS connected?
  const prevState = useRef({});
  const bell = useRef(null);
  const tickRef = useRef(0);
  const wsRef = useRef(null);
  const pollRef = useRef(null);
  const [, force] = useState(0);

  const handlePayload = (now) => {
    if (!now) return;
    if (!muted && bell.current) {
      for (const c of now.courts || []) {
        const prev = prevState.current[c.id];
        const shouldBuzz = (
          (prev === "live" && c.state !== "live") ||
          (prev === "ready" && c.state === "live")
        );
        if (shouldBuzz) {
          try { bell.current.currentTime = 0; bell.current.play().catch(() => {}); } catch {}
        }
        prevState.current[c.id] = c.state;
      }
    }
    setData(now);
    // Iter297 — OPS-driven default (SCREEN ROWS). Only apply when the
    // local viewer hasn't set their own override.
    try {
      const userFlag = window.localStorage.getItem(`rl_viewer_cpr_user_${id}`);
      if (userFlag !== "1") {
        const opsN = Number(now?.config?.viewer_columns);
        if (opsN >= 1 && opsN <= 6) {
          setCourtsPerRow(opsN);
        }
      }
    } catch {}
    tickRef.current = 0;
  };

  const httpLoad = async () => {
    try {
      const r = await axios.get(`${BACKEND}/api/festival/${id}/public`);
      handlePayload(r.data);
    } catch {
      // preserve last-known state — offline resilience for wall-mounts
    }
  };

  useEffect(() => {
    let cancelled = false;
    // Try WebSocket first; fall through to polling if it fails.
    const url = `${WS_BACKEND}/api/festival/${id}/public/ws`;
    let ws;
    try {
      ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => setLive(true);
      ws.onmessage = (e) => {
        try { handlePayload(JSON.parse(e.data)); } catch {}
      };
      ws.onerror = () => {};
      ws.onclose = () => {
        setLive(false);
        if (cancelled) return;
        // Fall back to HTTP polling every 3s.
        if (!pollRef.current) {
          httpLoad();
          pollRef.current = setInterval(httpLoad, 3000);
        }
      };
    } catch {
      httpLoad();
      pollRef.current = setInterval(httpLoad, 3000);
    }
    // Kick off HTTP as a safety net if WS never opens.
    const kickoff = setTimeout(() => {
      if (!wsRef.current || wsRef.current.readyState !== 1) httpLoad();
    }, 400);
    return () => {
      cancelled = true;
      try { ws?.close(); } catch {}
      clearTimeout(kickoff);
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [id, muted]);

  useEffect(() => {
    const t = setInterval(() => { tickRef.current += 1; force((n) => n + 1); }, 1000);
    return () => clearInterval(t);
  }, []);

  // Iter246/251 — Auto-scroll loop. Independent from payload state so
  // WS updates never interrupt the scroll. Uses setInterval(16ms) rather
  // than requestAnimationFrame — rAF gets throttled by many TV browsers
  // (Samsung Tizen, LG webOS, background Chrome tabs) which made the
  // scroll appear stuck. setInterval keeps ticking regardless.
  // Iter252 — Prefer numeric px/s (from the admin slider) over the
  // coarse enum when both are set.
  const scrollSpeed = data?.config?.viewer_scroll_speed || "medium";
  const scrollPxS = data?.config?.viewer_scroll_px_s;
  const effectivePxS = (typeof scrollPxS === "number")
    ? scrollPxS
    : (scrollSpeed === "off" ? 0 : (SCROLL_SPEED_PX_S[scrollSpeed] ?? SCROLL_SPEED_PX_S.medium));
  const [scrollActive, setScrollActive] = useState(false);
  useEffect(() => {
    if (!effectivePxS || effectivePxS <= 0) { setScrollActive(false); return; }
    const pxPerSec = effectivePxS;
    let last = performance.now();
    let paused = false;
    let userInteractingUntil = 0;
    // Iter252b — Float accumulator, so sub-pixel movement per 16ms tick
    // (i.e. any px_s < ~60) doesn't get truncated to 0 by window.scrollY
    // rounding. Without this, low speeds "look active" but never scroll.
    let acc = window.scrollY;
    const bump = () => { userInteractingUntil = performance.now() + 4000; };
    window.addEventListener("wheel", bump, { passive: true });
    window.addEventListener("touchmove", bump, { passive: true });
    const tick = () => {
      const now = performance.now();
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      if (paused) return;
      if (now < userInteractingUntil) { acc = window.scrollY; return; }
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll <= 40) { if (scrollActive) setScrollActive(false); return; }
      if (!scrollActive) setScrollActive(true);
      acc += pxPerSec * dt;
      if (acc >= maxScroll) {
        paused = true;
        window.scrollTo(0, maxScroll);
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: "smooth" });
          setTimeout(() => { paused = false; acc = 0; last = performance.now(); }, 1800);
        }, 2000);
      } else {
        window.scrollTo(0, Math.floor(acc));
      }
    };
    const interval = setInterval(tick, 16);
    return () => {
      clearInterval(interval);
      window.removeEventListener("wheel", bump);
      window.removeEventListener("touchmove", bump);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectivePxS, id]);

  if (!data) return <div className="min-h-screen bg-black text-white p-10 flex items-center justify-center">Loading festival…</div>;
  const { festival, courts, config } = data;
  const ratingMode = config?.rating_display || "band";
  const ribbon = data.sponsor_ribbon;
  // Iter313 — Dual-zone sponsor lists. Fall back to empty arrays so the
  // `.length > 0` guards below don't crash when the festival has no
  // sponsors (backend omits `sponsor_ribbon` entirely in that case).
  const headerSponsors = ribbon?.header || [];
  const footerSponsors = ribbon?.footer || [];
  const rotateSeconds = Math.max(3, Math.min(120, ribbon?.rotate_seconds || 60));

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white p-4 sm:p-6" data-testid="festival-public-viewer" data-scroll-speed={scrollSpeed}>
      {headerSponsors.length > 0 && (
        <SponsorZone list={headerSponsors} rotate={rotateSeconds} zone="header" />
      )}<div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#CCFF00] flex items-center gap-2">
            RALLY-LAB · LIVE
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${live ? "bg-[#4CAF50] animate-pulse" : "bg-[#FFB020]"}`} data-testid="viewer-ws-indicator" title={live ? "Live (WebSocket)" : "Polling"} />
            <span className="text-[#525252] hidden sm:inline" data-testid="viewer-scroll-label">
              · SCROLL {scrollSpeed.toUpperCase()}
              {scrollActive && (
                <span className="ml-2 px-1.5 py-0.5 border border-[#CCFF00] text-[#CCFF00] animate-pulse" data-testid="viewer-scroll-active">
                  ● AUTO-SCROLLING
                </span>
              )}
            </span>
          </div>
          <h1 className="h-display text-3xl sm:text-5xl xl:text-6xl">{festival.name}</h1>
          {festival.venue_name && <div className="text-base sm:text-xl text-[#A3A3A3]">📍 {festival.venue_name}</div>}
        </div>
        <button
          type="button"
          onClick={() => setMuted((m) => !m)}
          data-testid="viewer-mute-toggle"
          className="text-[10px] uppercase tracking-wider border border-[#262626] px-3 py-1.5 hover:border-[#CCFF00] hover:text-[#CCFF00]"
        >
          Buzzer · {muted ? "Muted" : "On"}
        </button>
      </div>
      {/* Iter294 — Courts per row selector (1-6, no auto). */}
      <div className="mb-3 flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#A3A3A3]">
        <span>Courts per row:</span>
        <div className="flex gap-1" data-testid="viewer-cpr">
          {[1, 2, 3, 4, 5, 6].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setCpr(n)}
              data-testid={`viewer-cpr-${n}`}
              className={`px-2 py-0.5 border ${courtsPerRow === n ? "bg-[#CCFF00] text-black border-[#CCFF00] font-bold" : "border-[#262626] text-[#A3A3A3] hover:border-[#CCFF00] hover:text-[#CCFF00]"}`}
            >{n}</button>
          ))}
        </div>
      </div>
      {/* Iter250/252/293/294/295 — Responsive card grid. HARD-CAP 6.
          User-controlled `courtsPerRow` (1-6, default 4). */}
      {console.log("[viewer-grid] render with courtsPerRow =", courtsPerRow) || null}
      <div
        className="gap-4 sm:gap-6"
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${Math.min(6, courtsPerRow)}, minmax(0, 1fr))`,
          gridAutoRows: "minmax(0, 1fr)",
          alignItems: "stretch",
          alignContent: "start",
        }}
        data-testid="viewer-court-grid"
        data-forced-cols={courtsPerRow}
      >
        {courts.map((c) => {
          const isReady = c.state === "ready" || c.state === "pre_start";
          const isLive = c.state === "live";
          // Iter189 — Big clock: game duration by default, ticking only
          // when live. Small clock: 5-min pre-start countdown, only in
          // `ready`. Both tick locally between WS/HTTP messages.
          const bigSeconds = isLive
            ? Math.max(0, (c.remaining_seconds || 0) - tickRef.current)
            : (c.game_duration_seconds ?? null);
          const preStartSeconds = isReady
            ? Math.max(0, (c.remaining_seconds || 0) - tickRef.current)
            : null;
          // Iter208 — Mirror the Live Ops flash on the wall display:
          // amber pulse when a live game has ≤30s left, green pulse
          // for 45s after Next-Up gets auto-refilled.
          const amberWarn = isLive && bigSeconds != null && bigSeconds <= 30;
          let greenFlash = false;
          if (c.next_up_filled_at) {
            try {
              const age = (Date.now() - new Date(c.next_up_filled_at).getTime()) / 1000;
              greenFlash = age >= 0 && age < 45;
            } catch {}
          }
          const flashClass = amberWarn
            ? "animate-pulse ring-4 ring-[#FFB020]"
            : greenFlash
            ? "animate-pulse ring-4 ring-[#4CAF50]"
            : "";
          const bannerClass = amberWarn
            ? "bg-[#FFB020] text-black"
            : greenFlash
            ? "bg-[#4CAF50] text-black"
            : isReady
            ? "bg-[#0F4B0F] text-[#CCFF00]"
            : "";
          const bannerText = amberWarn
            ? "◆ ENDING SOON"
            : greenFlash
            ? "◉ NEW LINE-UP"
            : isReady
            ? "⚡ PRE-START"
            : "";
          // Iter261 — Public Viewer status banner for non-active courts.
          const statusBanner = c.status && c.status !== "active"
            ? (c.status === "offline" ? "OFFLINE"
                : c.status === "maintenance" ? "MAINTENANCE"
                : "RESERVED · COACHING")
            : null;
          return (
            <div key={c.id} data-testid={`viewer-court-${c.id}`} data-status={c.status || "active"} data-flash={amberWarn ? "amber" : greenFlash ? "green" : ""} className={`bg-white text-black border-2 border-[#262626] rounded-md p-4 sm:p-6 flex flex-col relative overflow-hidden ${flashClass} ${statusBanner ? "opacity-60" : ""}`}>
              {statusBanner && (
                <div data-testid={`viewer-status-banner-${c.id}`} className="absolute inset-x-0 top-1/3 bg-[#FF4500] text-white text-center uppercase tracking-widest py-4 sm:py-6 font-black rotate-[-6deg] shadow-2xl" style={{ fontSize: `${2.5 * scale}rem` }}>
                  {statusBanner}
                </div>
              )}              {bannerText && (
                <div className={`uppercase tracking-widest text-center py-1 sm:py-1.5 -mx-4 sm:-mx-6 -mt-4 sm:-mt-6 mb-2 sm:mb-3 ${bannerClass}`} style={{ fontSize: `${0.75 * scale}rem` }} data-testid={`viewer-banner-${c.id}`}>
                  {bannerText}
                </div>
              )}
              <div className="font-bold text-center uppercase tracking-widest text-[#525252]" style={{ fontSize: `${1.6 * scale}rem` }}>{c.name}</div>
              {isReady && (
                <div className="flex items-center justify-center gap-2 mt-2 mb-1" data-testid={`viewer-prestart-${c.id}`}>
                  <span className="uppercase tracking-widest text-[#525252]" style={{ fontSize: `${0.7 * scale}rem` }}>Starts in</span>
                  <span className="font-mono text-[#CCFF00] bg-black px-2 rounded" style={{ fontSize: `${1.9 * scale}rem` }}>{fmt(preStartSeconds)}</span>
                </div>
              )}
              <div className={`font-mono text-center py-3 sm:py-4 rounded ${STATE_STYLES[c.state] || STATE_STYLES.idle}`} style={{ fontSize: `${4.5 * scale}rem`, lineHeight: 1 }}>
                {fmt(bigSeconds)}
              </div>
              <div className={`text-center uppercase tracking-widest mt-1 mb-3 font-bold ${isLive ? "text-[#FF3B30]" : (c.state === "finished" || c.state === "timeout") ? "text-[#FFB020]" : "text-[#525252]"}`} style={{ fontSize: `${0.95 * scale}rem` }}>
                {isLive ? "● LIVE" : (c.state === "finished" || c.state === "timeout") ? "◆ FINISHED" : c.state.toUpperCase()}
              </div>
              <div className="space-y-1 sm:space-y-2" style={{ minHeight: `${120 * scale}px` }}>
                {(c.current_players || []).length === 0 ? (
                  <div className="text-[#525252] italic text-center" style={{ fontSize: `${1.0 * scale}rem` }}>Empty court</div>
                ) : (c.current_players || []).map((p, i) => (
                  <PlayerLine key={i} p={p} ratingMode={ratingMode} scale={scale} />
                ))}
              </div>
              {(c.next_up_players || []).length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#262626]">
                  <div className="uppercase tracking-widest text-[#525252] text-center mb-1" style={{ fontSize: `${0.7 * scale}rem` }}>Next Up</div>
                  <div className="text-center leading-tight line-clamp-2 break-words" style={{ fontSize: `${1.0 * scale}rem` }} data-testid="viewer-next-up-names">
                    {(c.next_up_players || []).map((p, i) => (
                      <span key={i}>{typeof p === "string" ? p : p.name}{i < c.next_up_players.length - 1 ? ", " : ""}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <audio ref={bell} preload="auto" src="data:audio/wav;base64,UklGRnQEAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YVAEAAAAAP//AQAAAP7//v////7//v////7///8AAP//AAAAAP///v/+//7//v//////AAAAAAAAAAAAAP///v/+/////v////7//v//////AAAAAAAAAP///v/+/////v//////AAAAAAAAAAAAAP///v/+/////v/+//7////+//////8AAAAAAAAAAAAAAAAAAP///v/+/////v/+////AAAAAAAAAAAAAP//" />
      {/* Iter339 · Bug 16a — Coaching schedule block on the public
          viewer. Attendees can see the coaching sessions scheduled
          for the day from the venue screen. Sorted by start time,
          past sessions dropped. */}
      <CoachingScheduleStrip courts={courts} scale={scale} />
      <div className="mt-6 text-[10px] text-[#525252] text-center uppercase tracking-widest">
        {live ? "Live · WebSocket" : "Polling · 3s"} · {new Date(data.generated_at).toLocaleTimeString()}
      </div>
      {footerSponsors.length > 0 && (
        <SponsorZone list={footerSponsors} rotate={rotateSeconds} zone="footer" />
      )}
    </div>
  );
}

/* Iter300/302/313 — Sponsor Zone (rotating). One instance per zone
 * (header / footer). Each zone cycles its assigned sponsors on its own
 * timer with a 500ms fade transition between cards. If the zone has a
 * single sponsor the timer never fires — it just pins. Logo urls are
 * emitted with /api prefix by the backend; we grandfather any legacy
 * /uploads/... value client-side too. */
function classifyContact(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return { kind: "url", href: s, label: s.replace(/^https?:\/\//i, "") };
  if (/^[a-z0-9-]+(\.[a-z0-9-]+)+(\/[^\s]*)?$/i.test(s)) {
    return { kind: "url", href: `https://${s}`, label: s };
  }
  if (/^(\+?\d[\d\s()-]{6,})$/.test(s)) {
    return { kind: "tel", href: `tel:${s.replace(/\s+/g, "")}`, label: s };
  }
  return { kind: "note", href: null, label: s };
}

function resolveLogo(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("/api/")) return `${BACKEND}${s}`;
  if (s.startsWith("/uploads/")) return `${BACKEND}/api${s}`;
  if (s.startsWith("/")) return `${BACKEND}${s}`;
  return s;
}

function SponsorZone({ list, rotate, zone }) {
  // Iter313.1 — Auto grouping per user spec: 1 solo, 2-3 all shown
  // side-by-side (no rotation), 4+ rotate in groups of 3 every ~60s.
  // Each "page" fades out then in as a whole so the row change feels
  // like one motion. Empty list → render nothing.
  const GROUP = 3;
  const pages = list.length <= GROUP
    ? [list]
    : Array.from({ length: Math.ceil(list.length / GROUP) }, (_, i) => list.slice(i * GROUP, i * GROUP + GROUP));
  const [pageIdx, setPageIdx] = useState(0);
  const [fade, setFade] = useState("in");
  useEffect(() => {
    if (pages.length <= 1) return undefined;
    const cadence = Math.max(3, rotate || 60) * 1000;
    const t = setInterval(() => {
      setFade("out");
      setTimeout(() => {
        setPageIdx((i) => (i + 1) % pages.length);
        setFade("in");
      }, 500);
    }, cadence);
    return () => clearInterval(t);
  }, [pages.length, rotate]);
  if (!list.length) return null;
  const active = pages[pageIdx % pages.length];
  const cls = `border border-[#262626] bg-[#141414] px-4 py-3 rounded transition-opacity duration-500 ${fade === "out" ? "opacity-0" : "opacity-100"}`;
  const wrap = zone === "header" ? "mb-4" : "mt-4";
  return (
    <div className={wrap} data-testid={`sponsor-zone-${zone}`} data-position={zone} data-count={list.length} data-page-count={pages.length}>
      <div className={cls} data-testid={`sponsor-${zone}-static`}>
        <div className="flex items-center gap-4 min-w-0">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#CCFF00] shrink-0">Proudly supported by</span>
          <div className="flex items-stretch gap-6 min-w-0 flex-1 justify-around">
            {active.map((s) => <SponsorCard key={s.id} sponsor={s} zone={zone} />)}
          </div>
          {pages.length > 1 && (
            <div className="ml-auto flex gap-1 shrink-0" data-testid={`sponsor-${zone}-dots`}>
              {pages.map((_, i) => (
                <span
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full ${i === (pageIdx % pages.length) ? "bg-[#CCFF00]" : "bg-[#262626]"}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* Iter313.1 — Individual sponsor card. Extracted so header + footer
 * groups can render multiple in one row (spec: up to 3 side-by-side).
 * Falls through as a link when the sponsor has a URL/tel contact,
 * otherwise a static div — mirrors the old single-card behaviour. */
function SponsorCard({ sponsor, zone }) {
  const contact = classifyContact(sponsor.contact || sponsor.link_url);
  const logoSrc = resolveLogo(sponsor.logo_url);
  const inner = (
    <div className="flex items-center gap-3 min-w-0">
      {logoSrc && (
        <img
          src={logoSrc}
          alt={sponsor.name}
          className="h-12 sm:h-16 max-w-[180px] object-contain bg-white p-1.5 rounded shrink-0"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      )}
      <div className="min-w-0">
        <div className="text-sm sm:text-base font-semibold truncate text-white">{sponsor.name}</div>
        {contact && (
          <div className="text-[11px] sm:text-xs text-[#A3A3A3] truncate">{contact.label}</div>
        )}
      </div>
    </div>
  );
  const wrapCls = "px-2";
  if (contact?.href) {
    return (
      <a
        href={contact.href}
        target={contact.kind === "url" ? "_blank" : undefined}
        rel="noopener noreferrer"
        className={`${wrapCls} hover:opacity-80 transition-opacity`}
        data-testid={`sponsor-${zone}-card-${sponsor.id}`}
      >
        {inner}
      </a>
    );
  }
  return (
    <div className={wrapCls} data-testid={`sponsor-${zone}-card-${sponsor.id}`}>
      {inner}
    </div>
  );
}


/* Iter339 · Bug 16a — Coaching schedule strip on the public viewer.
 * Shows upcoming coaching sessions for the day (past ones dropped).
 * Multi-court sessions collapse to one row via coaching_group_id so
 * a single session spanning courts 1+2 renders once. */
function CoachingScheduleStrip({ courts, scale = 1 }) {
  const sessions = useMemo(() => {
    const now = Date.now();
    const filtered = (courts || []).filter((c) => {
      if (!c.coaching_slot_at) return false;
      try {
        const start = new Date(c.coaching_slot_at).getTime();
        const dur = (c.coaching_duration_minutes || 60) * 60000;
        return (start + dur) > now - 60000;
      } catch { return false; }
    });
    const groups = new Map();
    for (const cc of filtered) {
      const key = cc.coaching_group_id || `solo::${cc.id}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(cc);
    }
    return Array.from(groups.entries())
      .map(([key, list]) => {
        list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        return { key, anchor: list[0], courts: list };
      })
      .sort((a, b) => new Date(a.anchor.coaching_slot_at || 0) - new Date(b.anchor.coaching_slot_at || 0));
  }, [courts]);

  if (sessions.length === 0) return null;
  const fmtT = (iso) => {
    try {
      return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };
  return (
    <section
      data-testid="viewer-coaching-schedule"
      className="mt-6 border-2 border-[#CCFF00] bg-black p-4"
    >
      <div className="uppercase tracking-widest text-[#CCFF00] font-bold mb-3" style={{ fontSize: `${0.9 * scale}rem` }}>
        Coaching Schedule · {sessions.length} session{sessions.length === 1 ? "" : "s"}
      </div>
      <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2" data-testid="viewer-coaching-schedule-list">
        {sessions.map(({ key, anchor: c, courts: linked }) => {
          const levelLabel = c.coaching_level === "u35" ? "U3.5" : c.coaching_level === "o35" ? "O3.5" : "";
          const courtLabels = linked.map((x) => x.name).join(" + ");
          const totalSignups = linked.reduce((sum, x) => sum + ((x.coaching_signup_ids || []).length), 0);
          const totalCap = linked.reduce((sum, x) => sum + (x.coaching_max_players || 6), 0);
          return (
            <li
              key={key}
              data-testid={`viewer-coaching-row-${c.id}`}
              className="border border-[#262626] bg-[#0F0F0A] p-3 flex items-start gap-3"
            >
              <div className="font-mono text-[#CCFF00] shrink-0" style={{ fontSize: `${1.1 * scale}rem` }}>
                {fmtT(c.coaching_slot_at)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-white truncate" style={{ fontSize: `${0.95 * scale}rem` }}>
                  {c.coaching_session_name || "Untitled session"}
                  {levelLabel && <span className="ml-2 text-[#A3A3A3] text-[10px]">· {levelLabel}</span>}
                </div>
                <div className="text-[#A3A3A3] text-[11px] truncate">
                  {c.coaching_coach_name && <>Coach {c.coaching_coach_name} · </>}
                  {courtLabels}
                </div>
                <div className="text-[#525252] text-[10px] mt-0.5">
                  {totalSignups}/{totalCap} registered · {c.coaching_duration_minutes || 60} min
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
